import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Flag, AlertTriangle } from 'lucide-react';

export type ReportTargetType = 'message' | 'user' | 'voice';

export interface ReportTarget {
  type: ReportTargetType;
  id: string;
  reported_user_id?: string;
  /** Preview label shown in the modal header */
  label: string;
  /** For message reports — the message text shown as context */
  content?: string;
}

interface ReportModalProps {
  target: ReportTarget;
  onClose: () => void;
}

const REASONS: { value: string; label: string; desc: string }[] = [
  { value: 'spam',                 label: 'Spam',                      desc: 'Contenu répété ou non sollicité' },
  { value: 'harassment',           label: 'Harcèlement',               desc: 'Ciblage, intimidation ou abus répété' },
  { value: 'inappropriate_content',label: 'Contenu inapproprié',       desc: 'Contenu choquant ou indécent' },
  { value: 'hate_speech',          label: 'Discours haineux',          desc: 'Propos discriminatoires ou haineux' },
  { value: 'threat',               label: 'Menace',                    desc: 'Menaces de violence ou de dommage' },
  { value: 'self_harm',            label: 'Automutilation / Suicide',  desc: 'Promotion ou sollicitation de préjudice' },
  { value: 'csam',                 label: 'Contenu impliquant des mineurs', desc: 'Contenu sexuel mettant en scène des mineurs' },
  { value: 'misinformation',       label: 'Désinformation',            desc: 'Fausses informations dangereuses' },
  { value: 'other',                label: 'Autre',                     desc: 'Autre violation des CGU' },
];

const TARGET_LABEL: Record<ReportTargetType, string> = {
  message: 'message',
  user:    'utilisateur',
  voice:   'appel vocal',
};

const ReportModal: React.FC<ReportModalProps> = ({ target, onClose }) => {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiBase = import.meta.env.VITE_API_URL || '/api';
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    'Content-Type': 'application/json',
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/reports`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          target_type: target.type,
          target_id: target.id,
          reported_user_id: target.reported_user_id || undefined,
          reason,
          details: details.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Erreur');
      setSubmitted(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70" onClick={onClose}>
      <div
        className="bg-bg-primary border border-border-color rounded-xl w-full max-w-md shadow-2xl flex flex-col max-h-[90vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border-color">
          <div className="flex items-center gap-2">
            <Flag className="h-4 w-4 text-error" />
            <h2 className="font-bold text-text-primary">Signaler un {TARGET_LABEL[target.type]}</h2>
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        {submitted ? (
          <div className="p-8 text-center space-y-3">
            <div className="w-14 h-14 rounded-full bg-success/20 border border-success/30 flex items-center justify-center mx-auto">
              <Flag className="h-6 w-6 text-success" />
            </div>
            <h3 className="font-bold text-text-primary">Signalement envoyé</h3>
            <p className="text-sm text-text-secondary">
              Merci. Notre équipe de modération examinera ce signalement dans les plus brefs délais.
            </p>
            <button onClick={onClose} className="btn btn-secondary text-sm mt-2">Fermer</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1">
            {/* Context preview */}
            {target.content && (
              <div className="bg-bg-secondary border border-border-color rounded-lg p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-1">Contenu signalé</p>
                <p className="text-sm text-text-primary line-clamp-3 break-words">{target.content}</p>
                <p className="text-xs text-text-muted mt-1 font-mono">{target.label}</p>
              </div>
            )}
            {!target.content && (
              <div className="bg-bg-secondary border border-border-color rounded-lg p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-1">Signalement</p>
                <p className="text-sm text-text-primary">{target.label}</p>
              </div>
            )}

            {/* Reason */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-text-muted block mb-2">
                Raison <span className="text-error">*</span>
              </label>
              <div className="space-y-1.5">
                {REASONS.map(r => (
                  <label
                    key={r.value}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                      reason === r.value
                        ? 'border-accent-color bg-accent-color/10'
                        : 'border-border-color hover:border-border-color/80 bg-bg-secondary'
                    }`}
                  >
                    <input
                      type="radio"
                      name="reason"
                      value={r.value}
                      checked={reason === r.value}
                      onChange={() => setReason(r.value)}
                      className="accent-accent-color flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-text-primary">{r.label}</p>
                      <p className="text-xs text-text-muted">{r.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Optional details */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-text-muted block mb-1">
                Détails supplémentaires (optionnel)
              </label>
              <textarea
                value={details}
                onChange={e => setDetails(e.target.value)}
                rows={3}
                maxLength={1000}
                placeholder="Décrivez le problème plus précisément..."
                className="w-full px-3 py-2 bg-bg-secondary border border-border-color rounded-lg text-sm text-text-primary focus:outline-none focus:border-accent-color resize-none"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-error text-sm bg-error/10 border border-error/30 rounded-lg p-3">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="flex-1 btn btn-secondary text-sm">
                Annuler
              </button>
              <button
                type="submit"
                disabled={!reason || submitting}
                className="flex-1 btn bg-error hover:bg-error/80 text-white text-sm flex items-center justify-center gap-2 disabled:opacity-40"
              >
                {submitting
                  ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <Flag className="h-4 w-4" />
                }
                Envoyer le signalement
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  , document.body);
};

export default ReportModal;
